import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { type BuilderContent } from '@builder.io/sdk-angular';
import { Content } from '@builder.io/sdk-angular';
import { environment } from '../../../environments/environment';
import { 
  BlogArticleComponent, 
  type BlogAuthor, 
  type BlogData,
  type TopicThemePair
} from '../../components/blog-article/blog-article.component';
import { BlogPromoSectionComponent } from '../../components/blog-promo-section/blog-promo-section.component';
import { BlogSymbolSectionComponent } from '../../components/blog-symbol-section/blog-symbol-section.component';
import { AudienceService } from '../../services/audience.service';

/**
 * BlogPageComponent - Container component for blog articles
 * 
 * Fetches blog data via resolver and renders article with taxonomy and promo sections
 * Uses audience-based header/footer symbols:
 * - High School (bfwpub): BFW Blog Header / BFW Blog Footer
 * - Higher Ed (macmillan): Macmillan Blog Header / Macmillan Blog Footer
 */
@Component({
  selector: 'app-blog-page',
  templateUrl: './blog-page.component.html',
  styleUrls: ['./blog-page.component.scss'],
  standalone: true,
  imports: [CommonModule, Content, BlogArticleComponent, BlogPromoSectionComponent, BlogSymbolSectionComponent]
})
export class BlogPageComponent implements OnInit {
  article: BuilderContent | null = null;
  model = 'blog';
  apiKey = environment.builderApiKey;

  // Audience-based symbol names
  headerSymbolName: string = '';
  footerSymbolName: string = '';

  // Article content properties
  title: string = '';
  excerpt?: string;
  publishedDate?: string;
  author?: BlogAuthor; // Changed from authors array to single author
  contentHtml?: string;
  
  // Taxonomy properties
  discipline?: string;
  subject?: string;
  topics: string[] = [];
  themes: string[] = [];
  product?: string;
  contentSeries?: string;

  constructor(
    private route: ActivatedRoute,
    private audienceService: AudienceService,
    private titleService: Title,
    private metaService: Meta
  ) {
    // Set header/footer symbols based on audience
    if (this.audienceService.isHighSchool()) {
      this.headerSymbolName = 'BFW Blog Header';
      this.footerSymbolName = 'BFW Blog Footer';
    } else {
      this.headerSymbolName = 'Macmillan Blog Header';
      this.footerSymbolName = 'Macmillan Blog Footer';
    }
  }

  ngOnInit(): void {
    this.route.data.subscribe((data) => {
      this.article = data['blogArticle'];
      if (this.article) {
        this.extractMetadata();
      }
    });
  }

  /**
   * Extracts metadata and taxonomy from Builder.io article data
   * Handles different field types (text, select, reference)
   */
  private extractMetadata(): void {
    if (!this.article) return;

    const data = this.article.data as BlogData;

    // Basic article fields
    this.title = data.title || this.article.name || 'Untitled Article';
    this.excerpt = data.excerpt;
    this.publishedDate = data.publishedDate;
    this.contentHtml = data.content;
    
    // Set document title with audience-appropriate suffix
    const siteName = this.audienceService.isHighSchool() ? 'BFW Publishers' : 'Macmillan Learning';
    this.titleService.setTitle(`${siteName} | ${this.title}`);
    
    // Set dynamic meta description from excerpt (when available)
    // Strip HTML tags and use plain text only
    if (this.excerpt) {
      const plainTextExcerpt = this.stripHtmlTags(this.excerpt);
      this.metaService.updateTag({ name: 'description', content: plainTextExcerpt });
    } else {
      // Remove description tag if no excerpt is available
      this.metaService.removeTag("name='description'");
    }
    
    // Taxonomy fields - handle reference and text types
    this.extractTaxonomyFields(data);
    
    // Author field - single reference
    this.extractAuthor(data);
    
    // Push metadata to dataLayer for analytics
    this.pushMetadataToDataLayer();
  }

  /**
   * Extracts taxonomy fields handling reference types
   * Handles the topicthemepairs list structure from Builder.io
   */
  private extractTaxonomyFields(data: BlogData): void {
    // Discipline (reference - mlcourse model uses 'coursename' field)
    if (this.audienceService.isHigherEd()) {
    this.discipline = data.discipline?.value?.data?.coursename || 
                      data.discipline?.value?.data?.name || 
                      data.discipline?.value?.data?.title;
    }
    else {
    this.subject = data.subject?.value?.name;
    }
    // Product (select field)
    this.product = data.product;
    
    // Content Series (reference)
    this.contentSeries = data.contentSeries?.value?.data?.name || 
                         data.contentSeries?.value?.data?.title;
    
    // Extract Topic-Theme Pairs from list
    if (data.topicthemepairs && Array.isArray(data.topicthemepairs)) {
      this.topics = [];
      this.themes = [];
      
      data.topicthemepairs.forEach((pair) => {
        // Extract topic from reference
        const topicName = pair.topic?.value?.data?.name || 
                         pair.topic?.value?.data?.title;
        if (topicName && !this.topics.includes(topicName)) {
          this.topics.push(topicName);
        }
        
        // Extract theme from reference
        const themeName = pair.theme?.value?.name;
        if (themeName) {
          this.themes.push(themeName);
        }
      });
    }
  }

  /**
   * Extracts and transforms author data from Builder.io single reference
   * Note: Field name is "authors" even though it's now a single reference
   */
  private extractAuthor(data: BlogData): void {
    if (data.authors) {
      const authorData = data.authors.value?.data;
      if (authorData && authorData.name) {
        this.author = {
          name: authorData.name,
          slug: authorData.slug,
          authorId: authorData.authorId,
          bio: authorData.bio,
          avatarUrl: authorData.headshot,
          // title: authorData.title
        };
      }
    }
  }

  /**
   * Strips HTML tags from text and returns plain text content
   * Also decodes HTML entities and normalizes whitespace
   */
  private stripHtmlTags(html: string): string {
    if (!html) return '';
    
    // Create a temporary DOM element to parse and extract text
    if (typeof document !== 'undefined') {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      let text = tempDiv.textContent || tempDiv.innerText || '';
      
      // Normalize whitespace (replace multiple spaces/newlines with single space)
      text = text.replace(/\s+/g, ' ').trim();
      
      return text;
    }
    
    // Fallback for server-side: use regex to strip tags
    // This is less reliable but works in SSR contexts
    let text = html
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .replace(/&amp;/g, '&') // Decode &amp;
      .replace(/&lt;/g, '<') // Decode &lt;
      .replace(/&gt;/g, '>') // Decode &gt;
      .replace(/&quot;/g, '"') // Decode &quot;
      .replace(/&#39;/g, "'") // Decode &#39;
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
    
    return text;
  }

  /**
   * Pushes blog post metadata to dataLayer for Google Tag Manager / Analytics
   * Formats taxonomy fields as arrays as required by the tracking specification
   */
 private pushMetadataToDataLayer(): void {
    // Helper function to convert single values to arrays
    const toArray = (value: string | undefined): string[] => {
      if (!value) return [];
      return Array.isArray(value) ? value : [value];
    };

    // Build dataLayer push object with all fields always present
    const dataLayerPush: any = {
      event: 'post_metadata',
      topic: this.topics.length > 0 ? this.topics : [],
      discipline: this.discipline ? toArray(this.discipline) : [],
      content_series: this.contentSeries ? toArray(this.contentSeries) : [],
      product: this.product ? toArray(this.product) : [],
      theme: this.themes.length > 0 ? this.themes : []
    };

    // Initialize dataLayer if it doesn't exist, then push metadata
    if (typeof window !== 'undefined') {
      if (!(window as any).dataLayer) {
        (window as any).dataLayer = [];
      }
      (window as any).dataLayer.push(dataLayerPush);
    }
  }
}
